// Kyte final-checkout call (POST kyte.checkout). For ancillariesAsStep
// providers the booking is already held server-side by book_held; this is the
// payment commit against that hold. The success response carries
// `kyteVoucherId`; checkout.vue reads `flightVoucherId` (Amadeus convention)
// so we copy the id across before resolving to keep the view provider-agnostic.

import { service_handler } from "@/services";
import { log } from "@/utilities/commons";
import { normalize, build_baggage_allowance_from_offer } from "./normalize";
import { extract_dictionaries } from "./dictionaries";
import {
  as_string,
  as_number,
  merge_dictionaries,
} from "../base/helpers";
import { fold_committed_ancillaries } from "./ancillaries";

export const book = (_provider, flight_offer, request) => {
  const {
    heldBooking = {},
    guest = {},
    payments = {},
    livePrice = "",
  } = request || {};
  const { validatingAirlineCodes = [], providerOfferId = "" } =
    flight_offer || {};
  // baggageAllowance isn't echoed back in /retrieve (only /details ships it),
  // so we stash it on the booking via additionalInfo and re-read it in
  // get_confirmation — keeps the confirm page brand/allowance honest without
  // a second /details fetch.
  const baggage_allowance = build_baggage_allowance_from_offer(flight_offer);
  const booking_data = {
    bookingId: heldBooking.id || "",
    airlineCode:
      validatingAirlineCodes.length > 0 ? validatingAirlineCodes[0] : "",
    offerId: providerOfferId,
    live_price: livePrice,
    payments,
  };
  if (baggage_allowance.length) {
    booking_data.additionalInfo = { baggageAllowance: baggage_allowance };
  }
  const body = {
    ...guest,
    bookingData: booking_data,
  };
  return service_handler("kyte.checkout", body, { track: 'checkout' }).then(
    (response) => {
      const { data = {} } = response || {};
      if (
        data.kyteVoucherId !== undefined &&
        data.flightVoucherId === undefined
      ) {
        // Return a fresh envelope — don't mutate caller-visible state.
        return { ...response, data: { ...data, flightVoucherId: data.kyteVoucherId } };
      }
      return response;
    },
  );
};

/**
 * Walk legs (OD1, OD2, …) in declared order and resolve each leg's
 * flightSolution[] base64 ids against the booking's flightSolutions map.
 * Falls back to Object.values when no legs are advertised so a malformed
 * booking still surfaces something.
 * @param {object} booking
 * @returns {Array}
 */
const order_solutions_by_legs = (booking) => {
  const safe = booking || {};
  const itinerary = safe.itinerary || {};
  const legs = itinerary.legs || {};
  const solutions_map = itinerary.flightSolutions || {};
  // Natural sort: "OD2" before "OD10". Lex sort would put OD10 first.
  const leg_ids = Object.keys(legs).sort((a, b) =>
    String(a).localeCompare(String(b), undefined, { numeric: true }),
  );
  if (!leg_ids.length) return Object.values(solutions_map);
  const seen = {};
  const out = [];
  leg_ids.forEach((leg_id) => {
    const leg = legs[leg_id] || {};
    const ids = Array.isArray(leg.flightSolutions) ? leg.flightSolutions : [];
    ids.forEach((id) => {
      if (seen[id]) return;
      const sol = solutions_map[id];
      if (!sol) return;
      seen[id] = true;
      out.push(sol);
    });
  });
  return out;
};

/**
 * Project booking pricingBreakdown[] rows down onto the search-time per-pax
 * priceBreakdown shape (`{code, type, amount}`), mapping the booking-side
 * `FARE_PRICE` code onto the offer-side `BASE` code so extract_base_amount /
 * extract_taxes add up.
 */
const build_pax_price_breakdown = (pricing_breakdown, pax_id) => {
  const rows = (pricing_breakdown || []).filter(
    (row) => row && row.passengerId === pax_id,
  );
  return rows.reduce((acc, row) => {
    const inner = Array.isArray(row.breakdown) ? row.breakdown : [];
    return acc.concat(
      inner.map((b) => {
        const safe = b || {};
        const code = as_string(safe.code) === "FARE_PRICE"
          ? "BASE"
          : as_string(safe.code);
        return {
          code,
          type: as_string(safe.type),
          amount: as_number(safe.amount),
        };
      }),
    );
  }, []);
};

/**
 * Synthesize a search/details-shaped offer from a confirmed booking so
 * kyte/normalize.js can produce a canonical FlightOffer. The booking shape
 * packs the same conceptual data under different keys (flightSolutions
 * object vs array, pricingBreakdown vs per-pax priceBreakdown, totalCost vs
 * totalPrice).
 */
const build_offer_from_booking = (booking, voucher_root) => {
  const safe = booking || {};
  const safe_voucher = voucher_root || {};
  const passengers = (safe.passengers || []).map((pax) => {
    const safe_pax = pax || {};
    const total_price = (safe.pricingBreakdown || [])
      .filter((row) => row && row.passengerId === safe_pax.id)
      .reduce((sum, row) => sum + as_number(row.price), 0);
    return {
      ...safe_pax,
      price: total_price,
      priceBreakdown: build_pax_price_breakdown(
        safe.pricingBreakdown,
        safe_pax.id,
      ),
    };
  });
  const ticketing_carrier = safe.ticketingCarrier || {};
  const owner =
    as_string(safe_voucher.airlineCode) ||
    as_string(ticketing_carrier.code) ||
    "";
  const itinerary = safe.itinerary || {};
  const legs = itinerary.legs || {};
  return {
    id: as_string(safe.id),
    flightSolutions: order_solutions_by_legs(safe),
    owner,
    currency: safe.currency || { code: "EUR", decimals: 2 },
    totalPrice: as_number(safe.totalCost),
    passengers,
    fares: safe.fares || {},
    servicingFees: [],
    priceGuaranteeExpiration: "",
    expiration: "",
    singleJourneyLeg: Object.keys(legs).length === 1,
  };
};

/**
 * Reshape Kyte's flat `firstName/lastName` + `contactInformation.{email,
 * phone[]}` into the Amadeus-shaped traveler (`name.*`, `contact.*`)
 * confirm.vue expects. Normalizes once at the boundary so the page stays
 * provider-agnostic.
 */
const normalize_traveler = (pax) => {
  const safe = pax || {};
  const contact = safe.contactInformation || {};
  const phone_in = Array.isArray(contact.phone) ? contact.phone : [];
  const phones = phone_in.map((p) => {
    const safe_p = p || {};
    return {
      countryCallingCode: as_string(safe_p.countryCode),
      number: as_string(safe_p.number),
      type: as_string(safe_p.type) || "Mobile",
    };
  });
  return {
    id: as_string(safe.id),
    dateOfBirth: as_string(safe.dateOfBirth),
    gender: as_string(safe.gender),
    title: as_string(safe.title),
    type: as_string(safe.type),
    name: {
      firstName: as_string(safe.firstName),
      middleName: as_string(safe.middleName),
      lastName: as_string(safe.lastName),
    },
    contact: {
      emailAddress: as_string(contact.email),
      phones,
    },
  };
};

/**
 * Surface the canonical confirmation shape from a /confirm response body.
 * Aliases upstream `kyteVoucher` to `voucher` (Amadeus convention), caches
 * dictionaries on the provider, adapts the booking to a search-shaped offer
 * for normalize, and exposes Amadeus-shaped travelers.
 * @param {object} provider
 * @param {object} body
 * @returns {object}
 */
export const get_confirmation = (provider, body) => {
  const safe_body = body || {};
  const { kyteVoucher: raw_voucher = {}, ...rest } = safe_body;
  const { orderData = {}, pnr, ...voucher_root } = raw_voucher;
  const { booking = {} } = orderData;
  merge_dictionaries(provider, extract_dictionaries(safe_body));

  let flight_offers = [];
  if (booking && booking.id) {
    const synthesized = build_offer_from_booking(booking, voucher_root);
    const { additionalInfo: echoed = {} } = booking;
    const { baggageAllowance: echoed_allowance = [] } = echoed || {};
    if (Array.isArray(echoed_allowance) && echoed_allowance.length) {
      synthesized.baggageAllowance = echoed_allowance;
    }
    try {
      const normalized = normalize(provider, synthesized);
      flight_offers = [fold_committed_ancillaries(normalized, booking)];
    } catch (e) {
      // Log BE-side regressions; fall through with an empty list rather than
      // crash the confirm page (the rest of the voucher metadata is useful).
      log("[kyte/checkout] get_confirmation: normalize failed", e);
      flight_offers = [];
    }
  }

  const travelers = Array.isArray(booking.passengers)
    ? booking.passengers.map(normalize_traveler)
    : [];

  return {
    ...rest,
    voucher: {
      ...voucher_root,
      flight_offers,
      travelers,
      tickets: Array.isArray(booking.tickets) ? booking.tickets : [],
      carrierReference: pnr,
    },
  };
};
